import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, AlertTriangle, Calendar, ChevronLeft, ChevronRight, Clock, Plus, TrendingUp, User, Users, FileText, Stethoscope, Brain, CheckCircle } from "lucide-react";
import { TriageManagement } from "./triage/TriageManagement";
import { DashboardCalendar } from "./DashboardCalendar";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Link } from "react-router-dom";
import { TriageSummaryCard } from "./triage/TriageSummaryCard";
import { format } from "date-fns";
import { PreVisitBrief } from "./clinical/PreVisitBrief";

// ✨ NEW: Next-Gen Triage Flow Components (temporarily disabled - fixing database schema)
// import { EnhancedPreVisitSummary } from "./triage/EnhancedPreVisitSummary";
// import { DocumentUploadPanel } from "./triage/DocumentUploadPanel";
// import { ContextualDataPanel } from "./triage/ContextualDataPanel";

// Type definition for appointments
interface Appointment {
  id: string | number;
  time: string;
  patient: string;
  reason: string;
  status: string;
  isTestAppointment?: boolean;
}

export function DoctorDashboard() {
  const { profile } = useAuth();
  const [calendarView, setCalendarView] = useState<"day" | "week">("day");
  
  // ENHANCED DEBUGGING: Log user profile on component mount
  useEffect(() => {
    console.log('🔍 DOCTOR DASHBOARD DEBUG - Current user profile:', { 
      id: profile?.id,
      email: profile?.email,
      role: profile?.role,
      clinic_id: profile?.clinic_id 
    });
  }, [profile]);

  // Fetch today's appointments
  const { data: todayAppointments = [] } = useQuery<Appointment[]>({
    queryKey: ['today-appointments', profile?.clinic_id, profile?.id],
    queryFn: async () => {
      const fetchAppointments = async () => {
        if (!profile?.id) {
          console.error('🔍 DOCTOR DASHBOARD DEBUG - No profile ID, cannot fetch appointments');
          return [];
        }
        
        try {
          console.log('🔍 DOCTOR DASHBOARD DEBUG - Fetching appointments for doctor:', profile.id, 'clinic:', profile.clinic_id);
          
          // ENHANCED DEBUGGING: Get ALL provider records to see what we're working with
          const { data: allProviders, error: providerListError } = await supabase
            .from('providers')
            .select('*');
            
          console.log('🔍 DOCTOR DASHBOARD DEBUG - All providers in the system:', {
            count: allProviders?.length || 0,
            providers: allProviders,
            error: providerListError
          });
          
          // First, find the provider record for this doctor's profile
          const { data: providerData, error: providerError } = await supabase
            .from('providers')
            .select('id')
            .eq('profile_id', profile.id)
            .single();
            
          console.log('🔍 DOCTOR DASHBOARD DEBUG - Provider lookup result:', {
            providerData, 
            providerError, 
            profileId: profile.id,
            lookupFailed: providerError || !providerData 
          });
          
          // ENHANCED DEBUGGING: Temporary solution - use any provider ID if we can't find one
          let providerIds = [];
          
          if (providerError || !providerData) {
            console.warn('🔍 DOCTOR DASHBOARD DEBUG - No provider found for current user, attempting alternative lookup');
            
            // Try to find ANY provider
            const { data: anyProvider } = await supabase
              .from('providers')
              .select('id')
              .limit(1);
              
            console.log('🔍 DOCTOR DASHBOARD DEBUG - Any provider search result:', anyProvider);
            
            // Also try direct doctor ID
            providerIds = [
              profile.id, // Try using profile ID directly
              anyProvider?.[0]?.id // Try using any provider ID
            ].filter(Boolean);
            
            console.log('🔍 DOCTOR DASHBOARD DEBUG - Fallback provider IDs:', providerIds);
          } else {
            providerIds = [providerData.id];
          }
          
          // ENHANCED DEBUGGING: Also search directly for the Elvin test appointment
          const { data: testAppointment, error: testApptError } = await supabase
            .from('appointments')
            .select('*')
            .eq('patient_id', '5e86c8c3-3675-4485-bc4b-8bf0670a0369')
            .single();
            
          console.log('🔍 DOCTOR DASHBOARD DEBUG - Elvin test appointment direct lookup:', {
            testAppointment,
            error: testApptError
          });
          
          // If we found the test appointment, add its provider_id to our search
          if (testAppointment?.provider_id && !providerIds.includes(testAppointment.provider_id)) {
            providerIds.push(testAppointment.provider_id);
          }
          
          console.log('🔍 DOCTOR DASHBOARD DEBUG - Will search for appointments with provider IDs:', providerIds);
          
          // ENHANCED DEBUGGING: First try to get ALL appointments
          const { data: allAppointments, error: allApptError } = await supabase
            .from('appointments')
            .select('*')
            .order('start_time', { ascending: true });
            
          console.log('🔍 DOCTOR DASHBOARD DEBUG - ALL appointments in system:', {
            count: allAppointments?.length || 0,
            error: allApptError
          });
          
          // Build OR filter for all provider IDs
          let orFilter = '';
          providerIds.forEach((id, index) => {
            if (index > 0) orFilter += ',';
            orFilter += `provider_id.eq.${id}`;
          });
          
          // ENHANCED DEBUGGING: Clearer query with provider IDs
          console.log('🔍 DOCTOR DASHBOARD DEBUG - OR filter for appointments query:', orFilter);
          
          // Query appointments with all known provider IDs
          const { data: appointments, error } = await supabase
            .from('appointments')
            .select(`
              id, 
              start_time, 
              end_time, 
              status, 
              notes,
              provider_id,
              patient_id,
              profiles!appointments_patient_id_fkey (
                first_name,
                last_name,
                email
              )
            `)
            .or(orFilter)
            .order('start_time', { ascending: true });
            
          console.log('🔍 DOCTOR DASHBOARD DEBUG - Appointments query result:', { 
            count: appointments?.length || 0, 
            appointments: appointments,
            orFilter: orFilter,
            error: error,
            providerIds: providerIds
          });
            
          if (error) {
            console.error('🔍 DOCTOR DASHBOARD DEBUG - Error fetching appointments:', error);
            return [];
          }
          
          if (!appointments || appointments.length === 0) {
            console.log('🔍 DOCTOR DASHBOARD DEBUG - No appointments found with any provider_id');
            
            // ENHANCED DEBUGGING: Try direct appointment fetch one more time
            const { data: directAppt } = await supabase
              .from('appointments')
              .select('*')
              .eq('id', 'aaa3550c-7234-4ad2-bbd5-b2b81b0f9579')
              .single();
              
            console.log('🔍 DOCTOR DASHBOARD DEBUG - Direct appointment lookup:', directAppt);
            return [];
          }
          
          // Transform to expected format
          const formattedAppointments = appointments.map((apt: any) => {
            console.log('🔍 DOCTOR DASHBOARD DEBUG - Processing appointment:', apt);
            
            // Fix for potential timezone issues with appointment time
            const startTime = new Date(apt.start_time);
            const endTime = new Date(apt.end_time);
            
            const patientName = apt.profiles 
              ? `${apt.profiles.first_name || ''} ${apt.profiles.last_name || ''}`.trim() 
              : 'Unknown Patient';
            
            return {
              id: apt.id,
              time: startTime.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              }),
              date: startTime.toLocaleDateString('en-US'),
              patient: patientName,
              reason: apt.notes || 'General consultation',
              status: apt.status || 'scheduled',
              startTime: apt.start_time,
              endTime: apt.end_time,
              providerId: apt.provider_id,
              patientId: apt.patient_id
            };
          });
          
          console.log('🔍 DOCTOR DASHBOARD DEBUG - Final processed appointments:', {
            count: formattedAppointments.length,
            appointments: formattedAppointments.map(a => ({
              id: a.id,
              patient: a.patient,
              time: a.time,
              date: a.date,
              status: a.status,
              providerId: a.providerId,
              patientId: a.patientId
            }))
          });
          
          return formattedAppointments;
            
          } catch (err) {
            console.error('🔍 DOCTOR DASHBOARD DEBUG - Exception in appointments query:', err);
            return [];
          }
      };
      return fetchAppointments();
    },
    enabled: !!profile?.id
  });
  
  // ENHANCED DEBUGGING: Log today's appointments every time they change 
  useEffect(() => {
    console.log('🔍 DOCTOR DASHBOARD DEBUG - Today\'s appointments changed:', {
      count: todayAppointments?.length || 0,
      appointments: todayAppointments
    });
  }, [todayAppointments]);
  
  // Fetch patient count using the same proven RPC approach as Patient Management
  const { data: patientCount = 0 } = useQuery({
    queryKey: ['patient-count', profile?.clinic_id],
    queryFn: async () => {
      if (!profile?.clinic_id) return 0;
      
      try {
        console.log('DEBUG: Fetching patient count for clinic:', profile.clinic_id);
        
        // Use the same RPC function that works in Patient Management
        const { data: allPatientsData, error: rpcError } = await supabase
          .rpc('get_patients_by_clinic', { search_query: '' });
        
        if (allPatientsData && !rpcError) {
          console.log('DEBUG: Patient count RPC returned', allPatientsData.length, 'patients');
          return allPatientsData.length;
        } else {
          console.error('DEBUG: Patient count RPC failed:', rpcError?.message);
          return 0;
        }
      } catch (err) {
        console.error('Exception in patient count query:', err);
        return 0;
      }
    },
    enabled: !!profile?.clinic_id
  });

  // Fetch triage stats and recent submissions with audio
  console.log('DEBUG: About to create triage query with profile:', profile?.clinic_id, 'enabled:', !!profile?.clinic_id);
  
  const { data: triageData } = useQuery({
    queryKey: ['triage-data', profile?.clinic_id],
    queryFn: async () => {
      console.log('DEBUG: TRIAGE QUERY EXECUTING! Clinic ID:', profile?.clinic_id);
      
      if (!profile?.clinic_id) {
        console.log('DEBUG: No clinic_id, returning early');
        return { stats: null, recentSubmissions: [] };
      }

      try {
        console.log('DEBUG: Fetching triage submissions for clinic:', profile.clinic_id);
        
        // STEP 1: Get triage submissions for the last 24 hours
        console.log('DEBUG: Starting triage submissions query for clinic:', profile.clinic_id);
        
        // Use wildcard selection (same as TriageManagement) instead of specific columns
        let { data: submissions, error } = await supabase
          .from('triage_submissions')
          .select('*')
          .eq('clinic_id', profile.clinic_id)
          .order('created_at', { ascending: false })
          .limit(5); // Limit to 5 most recent submissions
        
        if (error) {
          console.error('DEBUG: Error in triage query:', error);
          return { stats: null, recentSubmissions: [] };
        }
        
        if (!submissions || submissions.length === 0) {
          console.log('DEBUG: No triage submissions found for clinic:', profile.clinic_id);
          return { stats: null, recentSubmissions: [] };
        }

        console.log(`DEBUG: Found ${submissions.length} triage submissions`);
        
        // STEP 2: Fetch patient profiles using the same proven RPC approach as Patient Management
        const profilesMap: Record<string, any> = {};
        
        if (submissions.length > 0) {
          console.log('DEBUG: Using get_patients_by_clinic RPC for patient data');
          
          // Use the same RPC function that works in Patient Management
          const { data: allPatientsData, error: rpcError } = await supabase
            .rpc('get_patients_by_clinic', { search_query: '' });
          
          if (allPatientsData && !rpcError) {
            console.log('DEBUG: RPC returned', allPatientsData.length, 'patients');
            // Filter to only the patients we need
            const relevantPatients = allPatientsData.filter(patient => 
              submissions.map(submission => submission.patient_id).includes(patient.id)
            );
            
            console.log('DEBUG: Filtered to', relevantPatients.length, 'relevant patients');
            
            // Map to profiles format
            relevantPatients.forEach(patient => {
              profilesMap[patient.id] = {
                id: patient.id,
                first_name: patient.first_name || '',
                last_name: patient.last_name || '',
                email: patient.email || ''
              };
            });
          } else {
            console.error('DEBUG: RPC failed:', rpcError?.message);
          }
        }
        
        // Fallback: If RPC didn't return all needed patients, try direct profiles query
        const missingPatientIds = submissions.map(submission => submission.patient_id).filter(id => !profilesMap[id]);
        
        if (missingPatientIds.length > 0) {
          console.log('DEBUG: Some patients missing from RPC, trying direct query for:', missingPatientIds);
          
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, email')
            .in('id', missingPatientIds);
          
          if (profilesData && !profilesError) {
            profilesData.forEach(profile => {
              profilesMap[profile.id] = profile;
            });
          }
          
          // Final fallback for any still missing patients
          missingPatientIds.forEach(id => {
            if (!profilesMap[id]) {
              // Try to parse email format (name.lastname@domain.com)
              if (id && typeof id === 'string' && id.includes('@') && id.includes('.')) {
                const namePart = id.split('@')[0];
                const parts = namePart.split('.');
                
                if (parts.length >= 2) {
                  profilesMap[id] = {
                    id,
                    first_name: parts[0].charAt(0).toUpperCase() + parts[0].slice(1),
                    last_name: parts[1].charAt(0).toUpperCase() + parts[1].slice(1),
                    email: id
                  };
                } else {
                  // Default fallback
                  profilesMap[id] = {
                    id,
                    first_name: 'Patient',
                    last_name: '',
                    email: id
                  };
                }
              } else {
                // Default fallback
                profilesMap[id] = {
                  id,
                  first_name: 'Patient', 
                  last_name: '',
                  email: ''
                };
              }
            }
          });
        }
        
        // STEP 3: Calculate triage stats
        const last24Hours = new Date();
        last24Hours.setHours(last24Hours.getHours() - 24);
        
        const { data: last24HoursData, error: statsError } = await supabase
          .from('triage_submissions')
          .select('id, status, urgency')
          .eq('clinic_id', profile.clinic_id)
          .gte('created_at', last24Hours.toISOString());
        
        if (statsError) {
          console.error('DEBUG: Error fetching stats data:', statsError);
        }
        
        const stats = {
          total: last24HoursData?.length || 0,
          urgent: last24HoursData?.filter(item => 
            item.urgency === 'very urgent' || item.urgency === 'immediate').length || 0,
          pending: last24HoursData?.filter(item => item.status === 'submitted').length || 0
        };

        // STEP 4: Map submission data with profile data
        const recentSubmissions = submissions.map(submission => {
          // If we have patient data from the join or direct query, use it
          if (profilesMap[submission.patient_id]) {
            return {
              ...submission,
              patient: profilesMap[submission.patient_id]
            };
          }
          
          // If patient_id looks like an email, extract name from it
          if (submission.patient_id && submission.patient_id.includes('@')) {
            const emailParts = submission.patient_id.split('@')[0].split('.');
            const firstName = emailParts[0] ? emailParts[0].charAt(0).toUpperCase() + emailParts[0].slice(1) : '';
            const lastName = emailParts[1] ? emailParts[1].charAt(0).toUpperCase() + emailParts[1].slice(1) : '';
            
            return {
              ...submission,
              patient: {
                id: submission.patient_id,
                first_name: firstName || 'Patient',
                last_name: lastName || '',
                email: submission.patient_id
              }
            };
          }
          
          // Last resort fallback - should rarely happen with the improved queries
          return {
            ...submission,
            patient: {
              id: submission.patient_id,
              first_name: 'Patient', 
              last_name: '',
              email: ''
            }
          };
        });
        
        console.log('DEBUG: Final submissions with patient data:', recentSubmissions.length);
        
        console.log('DEBUG: Final recent submissions with patient data:', recentSubmissions);

        return { stats, recentSubmissions };
      } catch (err) {
        console.error('Exception in triage data query:', err);
        return { 
          stats: {
            total: 0,
            urgent: 0,
            pending: 0
          }, 
          recentSubmissions: [] 
        };
      }
    },
    enabled: !!profile?.clinic_id
  });

  // Extract stats and submissions from the query result
  const triageStats = triageData?.stats || { total: 0, urgent: 0, pending: 0 };
  const recentTriageSubmissions = triageData?.recentSubmissions || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Doctor Dashboard</h1>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Today is {format(new Date(), 'EEEE, MMMM d, yyyy')}</span>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Today's Appointments</CardTitle>
            <Clock className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{todayAppointments.length}</div>
            <p className="text-xs text-muted-foreground">
              {todayAppointments.length > 0 ? "Next: " + todayAppointments[0]?.time : "No appointments"}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-amber-50 border-amber-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Triage</CardTitle>
            <AlertTriangle className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{triageStats?.pending || 0}</div>
            <p className="text-xs text-muted-foreground">
              {triageStats?.urgent || 0} urgent cases
            </p>
            {triageStats?.pending > 0 && (
              <Link to="#triage-section" className="text-xs text-blue-600 hover:underline mt-1 block">
                View pending cases
              </Link>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Patients</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{patientCount}</div>
            <p className="text-xs text-muted-foreground">Registered patients</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Triage Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{triageStats ? Math.round((triageStats.total / (patientCount || 1)) * 100) : 0}%</div>
            <p className="text-xs text-muted-foreground">Patient triage completion</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Dashboard Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Calendar View - Takes 2/3 of the space */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Calendar Overview
              </CardTitle>
              <CardDescription>
                Manage your schedule efficiently with day or week view
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex justify-end mb-2">
                <div className="flex items-center border rounded-md overflow-hidden">
                  <Button
                    variant={calendarView === "day" ? "default" : "outline"}
                    size="sm"
                    className="rounded-r-none border-r-0"
                    onClick={() => setCalendarView("day")}
                  >
                    Day
                  </Button>
                  <Button
                    variant={calendarView === "week" ? "default" : "outline"}
                    size="sm"
                    className="rounded-l-none"
                    onClick={() => setCalendarView("week")}
                  >
                    Week
                  </Button>
                </div>
              </div>
              <div className="mt-4">
                <DashboardCalendar compact={true} selectedView={calendarView} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions and Triage Responses */}
        <div className="space-y-6">
          {/* Clinical Quick Actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <Stethoscope className="h-5 w-5 text-blue-600" />
                Clinical Actions
              </CardTitle>
              <CardDescription>Frequently used clinical workflows</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2">
                <Link to="/calendar">
                  <Button className="w-full justify-start" variant="outline">
                    <Calendar className="w-4 h-4 mr-2" />
                    View Schedule
                  </Button>
                </Link>
                <Link to="/patients">
                  <Button className="w-full justify-start" variant="outline">
                    <Users className="w-4 h-4 mr-2" />
                    Patient Records
                  </Button>
                </Link>
                <Button className="w-full justify-start" variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  New Consultation
                </Button>
                <Button className="w-full justify-start" variant="outline">
                  <FileText className="w-4 h-4 mr-2" />
                  Clinical Notes
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Triage Statistics */}
          <Card className="border-amber-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Triage Statistics
              </CardTitle>
              <CardDescription>Last 24 hours triage activity</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col items-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-700">{triageStats?.total || 0}</div>
                  <div className="text-xs text-gray-500">Total</div>
                </div>
                <div className="flex flex-col items-center p-3 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-700">{triageStats?.urgent || 0}</div>
                  <div className="text-xs text-gray-500">Urgent</div>
                </div>
                <div className="flex flex-col items-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-700">{triageStats?.pending || 0}</div>
                  <div className="text-xs text-gray-500">Pending</div>
                </div>
              </div>
              <div className="mt-4">
                <Button 
                  className="w-full justify-center text-center" 
                  variant="outline"
                  onClick={() => document.getElementById('triage-section')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  View All Triage Cases
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Pre-Visit Summaries */}
      <div className="mt-12 mb-8">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-blue-600" />
          Pre-Visit Summaries
          {todayAppointments.length > 0 && (
            <Badge variant="outline" className="ml-2">
              {todayAppointments.length} Appointment{todayAppointments.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </h2>
        
        {todayAppointments.length > 0 ? (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {todayAppointments.map((appointment) => (
              <Card key={appointment.id} className="overflow-hidden">
                <div className="p-4 flex flex-col gap-3">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium text-lg">{appointment.patient}</h3>
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="h-3 w-3 mr-1" />
                        {appointment.time}
                      </div>
                    </div>
                    {appointment.isTestAppointment && (
                      <div className="text-xs text-purple-600 mb-1">
                        Test appointment from test_appointments.sql
                      </div>
                    )}
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Reason:</span> {appointment.reason}
                    </p>
                    <Badge 
                      variant={appointment.status === 'confirmed' ? 'default' : 'secondary'}
                      className="mt-2"
                    >
                      {appointment.status}
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // TODO: Generate AI summary
                        console.log('Generate AI summary for appointment:', appointment.id);
                      }}
                    >
                      <Brain className="h-4 w-4 mr-1" />
                      Generate Summary
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => {
                        // TODO: Review/approve summary
                        console.log('Review summary for appointment:', appointment.id);
                      }}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Review
                    </Button>
                  </div>
                </div>
                
                {/* Placeholder for AI-generated summary */}
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-2">Clinical Summary</h4>
                  <p className="text-sm text-blue-800">
                    {appointment.reason || 'AI summary will be generated based on patient history and appointment notes.'}
                  </p>
                  <div className="mt-2 text-xs text-blue-600">
                    Status: Ready for AI generation
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-6 text-center text-gray-500">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No appointments found</p>
            <p className="text-sm mt-1">This could be due to:</p>
            <ul className="text-sm mt-2 text-left mx-auto w-fit">
              <li>• No appointments scheduled</li>
              <li>• Provider ID mismatch</li>
              <li>• Test data not loaded in database</li>
            </ul>
            <p className="text-sm mt-3">Run test_appointments.sql in Supabase SQL Editor to create test data.</p>
          </Card>
        )}
      </div>

      {/* Recent Triage Submissions with Audio */}
      <div className="mt-12 mb-8">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          Recent Triage Submissions
          {triageStats?.urgent > 0 && (
            <Badge variant="destructive" className="ml-2">
              {triageStats.urgent} Urgent
            </Badge>
          )}
        </h2>
        
        {recentTriageSubmissions.length > 0 ? (
          <div className="grid gap-4">
            {recentTriageSubmissions.map((submission) => (
              <TriageSummaryCard
                key={submission.id}
                triageId={submission.id}
                patientName={`${submission.patient.first_name} ${submission.patient.last_name}`}
                patientEmail={submission.patient.email}
                symptoms={submission.symptoms || []}
                urgency={submission.urgency}
                status={submission.status}
                createdAt={submission.created_at}
                redFlags={submission.red_flags || []}
                audioUrl={submission.audio_url}
              />
            ))}
          </div>
        ) : (
          <Card className="p-6 text-center text-gray-500">
            <p>No recent triage submissions found</p>
          </Card>
        )}
      </div>
      
      {/* Triage Management with anchor for direct navigation */}
      <div id="triage-section">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          Triage Management
        </h2>
        <TriageManagement />
      </div>

      {/* Next-Gen Triage Flow - Database Setup Required */}
      <div className="mt-12 mb-8">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          Next-Gen Triage Flow - Database Setup Required
        </h2>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              ⚠️ Database Migration Required
            </CardTitle>
            <CardDescription>
              Components ready but need database schema setup first
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>✅ Components Created</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>✅ Dependencies Installed</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-600" />
                <span>⏳ Database Schema Migration Needed</span>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Status:</strong> Components temporarily disabled to prevent crashes.<br/>
                  <strong>Next Step:</strong> Run database migrations to create contextual_data table.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
